import { createListenerMiddleware, isAnyOf } from "@reduxjs/toolkit";
import {
    NotationState,
    notationActions,
    selectFileEntry,
    selectIsSavePending,
    selectSaveStatus,
} from "../slices/notation";

/**
 * The slice of state this middleware reads.
 *
 * Declared structurally rather than importing `RootState`, which would be a
 * circular import: the store mounts this middleware.
 */
type ListenerState = { notation: NotationState };

/**
 * Persistence as a reaction to notation changes.
 *
 * Components dispatch a notation action and nothing more. This listener decides
 * when the document reaches storage, which means:
 *
 *  - persistence is not called imperatively from action creators or components,
 *    so a save can outlive the component that triggered it;
 *  - the write itself goes through an injected port, so the Next.js migration
 *    swaps a Firestore call for a route-handler call without touching anything
 *    that dispatches;
 *  - there is no module-level timer map. "At most one queued save per file" is
 *    tracked in Redux state (`savePending`), which is per-file correct and
 *    carries no cross-request risk on a server.
 *
 * The write-rate cap is deliberate: Firestore bills per document write, not per
 * byte, so the interval is the only cost lever. A burst of keystrokes still
 * produces exactly one write per interval.
 */

/** The side of persistence this middleware needs. Injected, never imported. */
export interface NotationSavePort {
    /** Writes the current document. Resolves when the write settles. */
    save(fileId: string): Promise<void>;
    /** Milliseconds until a write is allowed again; 0 if allowed now. */
    cooldownRemainingMs(fileId: string): number;
}

/**
 * The middleware instance mounted by the store.
 *
 * It is created empty here and the effect is registered separately by
 * `registerNotationSaveListener`. Why: the effect needs the persistence port,
 * and the persistence module imports the store — so wiring the port in at
 * store-construction time would be a require cycle (store -> persistence ->
 * store), which resolves to `undefined` at runtime.
 */
export const notationSaveListener = createListenerMiddleware<ListenerState>();

/** Every action that changes the notation and therefore needs persisting. */
const MUTATION_ACTIONS = [
    notationActions.addSection,
    notationActions.deleteSection,
    notationActions.moveSectionUp,
    notationActions.moveSectionDown,
    notationActions.setSectionName,
    notationActions.addRow,
    notationActions.deleteRow,
    notationActions.moveRowUp,
    notationActions.moveRowDown,
    notationActions.rotateRowLeft,
    notationActions.rotateRowRight,
    notationActions.addTrackInRow,
    notationActions.removeTrackInRow,
    notationActions.updateValueAtCell,
    notationActions.insertSubCell,
    notationActions.removeSubCell,
    notationActions.rotateSubCells,
    notationActions.setSubRowName,
    notationActions.setSummaryTrackName,
    notationActions.setTrackType,
] as const;

/** Wires persistence to notation actions. Called once, at app start. */
export function registerNotationSaveListener(savePort: NotationSavePort) {
    notationSaveListener.startListening({
    matcher: isAnyOf(...MUTATION_ACTIONS),
    effect: async (action, api) => {
        // Every matched action carries a fileId; read it defensively so an
        // unexpected shape is ignored rather than throwing inside middleware.
        const fileId = (action as { payload?: { fileId?: string } }).payload
            ?.fileId;
        if (!fileId) return;
        const state = api.getState();

        const entry = selectFileEntry(state, fileId);
        // A read-only viewer never writes. Checked here rather than deeper so
        // no timer is ever armed for a document that cannot be saved.
        if (!entry || entry.readOnly) return;

        // Mark dirty immediately so the UI reflects the change before any wait.
        const status = selectSaveStatus(state, fileId);
        if (status) {
            api.dispatch(
                notationActions.saveStatusChanged({
                    fileId,
                    saveStatus: {
                        ...status,
                        status: "Pending Save",
                        lastChange: Date.now(),
                        changesMade: true,
                    },
                })
            );
        }

        // Exactly one queued write per file: a burst of edits does not become a
        // burst of writes. The flag is per-file, so another open document is
        // unaffected.
        if (selectIsSavePending(api.getState(), fileId)) return;

        const remaining = savePort.cooldownRemainingMs(fileId);
        if (remaining > 0) {
            api.dispatch(
                notationActions.savePendingChanged({ fileId, pending: true })
            );
            // Cancellable: if the document is closed mid-wait, the listener is
            // torn down with the store rather than firing at a missing entry.
            await api.delay(remaining);
            api.dispatch(
                notationActions.savePendingChanged({ fileId, pending: false })
            );

            // The document may have been closed while we waited.
            if (!selectFileEntry(api.getState(), fileId)) return;
        }

        await savePort.save(fileId);
    },
    });
}
